import { ExportStatus } from '@aibees/shared/components/status-tag';
import {
  Maybe,
  Nullable,
  PaginationRequest,
  PaginationResponse,
  UserRelation,
  Workspace,
} from '@aibees/shared/definition';
import { SocketMessageType } from '@aibees/shared/socket';
import { IntegrationExportSettingItem } from '../components/export-dialog/components/integration-export-setting';
import { Company } from '../feature/search-table-company';
import { Person } from '../feature/search-table-people';
import { CrawlRow } from '../types';
import { IntegrationType } from '../types/integration';
import { SearchMode } from '../types/search';

export interface ReadCompanyRequest {}

export type ReadCompanyResponse = Company;

export interface EmployeeTrendsChartDataItem {
  name: Maybe<string>;
  data: Nullable<number>[];
}

export interface ReadPersonRequest {}

export type ReadPersonResponse = Person;

export enum MetadataType {
  COMPANY_TYPE = 'COMPANY_TYPE',
  COMPANY_FUNDING_TYPE = 'COMPANY_FUNDING_TYPE',
  COMPANY_REVENUE = 'COMPANY_REVENUE',
  COMPANY_SOCIAL_MEDIA = 'COMPANY_SOCIAL_MEDIA',
  COMPANY_KEYWORD_SOURCE = 'COMPANY_KEYWORD_SOURCE',
  COMPANY_EMPLOYEE_SIZE = 'COMPANY_EMPLOYEE_SIZE',
  COMPANY_CONTINENT = 'COMPANY_CONTINENT',
  COMPANY_COUNTRY = 'COMPANY_COUNTRY',
  COMPANY_INDUSTRY = 'COMPANY_INDUSTRY',
  PERSON_SOCIAL_MEDIA = 'PERSON_SOCIAL_MEDIA',
  PERSON_CONTINENT = 'PERSON_CONTINENT',
  PERSON_COUNTRY = 'PERSON_COUNTRY',
  PERSON_SENIORITY = 'PERSON_SENIORITY',
  COMPANY_EMPLOYEE_SENIORITY = 'COMPANY_EMPLOYEE_SENIORITY',
  COMPANY_DEPARTMENT = 'COMPANY_DEPARTMENT', //deprecated
  PERSON_LANGUAGE = 'PERSON_LANGUAGE',
  COMPANY_LANGUAGE = 'COMPANY_LANGUAGE',
  PERSON_PROFILE_BADGE = 'PERSON_PROFILE_BADGE',
  PERSON_KEYWORD_SOURCE = 'PERSON_KEYWORD_SOURCE',
  COMPANY_LINKEDIN_FOLLOWERS = 'COMPANY_LINKEDIN_FOLLOWERS',
  PERSON_LINKEDIN_FOLLOWERS = 'PERSON_LINKEDIN_FOLLOWERS',
  PERSON_LINKEDIN_CONNECTIONS = 'PERSON_LINKEDIN_CONNECTIONS',
}

export interface ResponseMetadataItem {
  key: string;
  value: number;
}

// this interface is converted metadata item response
export interface MetadataItem {
  value: string;
  count?: number;
}

export type BrowseMetadataRequest = {
  metadataType: MetadataType;
  metadataValue?: string;
};

export type BrowseMetadataResponse = ResponseMetadataItem[];

export type BrowseSearchIdsRequest = {
  maxPeople?: number;
  size: number;
  sort?: string;
};

export type BrowseSearchIdsResponse = string[];

export enum ExportSearchGrouping {
  COMPANY = 'COMPANY',
  PERSON = 'PERSON',
  ALL = 'COMPANY,PERSON',
}

export enum ExportIntegrationStatus {
  DONE = 'DONE',
  PROCESSING = 'PROCESSING',
  FAILED = 'FAILED',
}

export type IntegrationSmartLeadSettingConfig = {
  tokenId: string;
  id: string;
  setting: {
    campaignId: string;
    settings: IntegrationExportSettingItem;
  };
  status: ExportIntegrationStatus;
  response?: {
    error?: string;
    ok: boolean;
    leadLimitExhausted: boolean;
  };
};

export type AddExportSearchRequestBody = {
  grouping: ExportSearchGrouping;
  name: string;
  ids: string[];
  overwriteOwner?: boolean;
  user?: UserRelation;
  actions?: SaveRecordAction[];
  labels?: string[];
  type?: string;
  metadata?: Record<string, any>;
  setting?: Nullable<Record<string, any>>;
  integrations?: Nullable<
    Partial<Record<IntegrationType, IntegrationSmartLeadSettingConfig>>
  >;
};

export type AddExportSearchRequestBodyWithIntegration<
  DynamicKey extends string
> = AddExportSearchRequestBody & {
  [K in DynamicKey]?: IntegrationSmartLeadSettingConfig;
};

export enum ExportLinkType {
  GENERAL = 'GENERAL',
  LINKEDIN = 'LINKEDIN',
  SALES = 'SALES',
}

export enum ExportVisibility {
  INTERNAL = 'INTERNAL',
  PRIVATE = 'PRIVATE',
  ALL = '',
}

export type ExportFiles = {
  [key in ExportLinkType]?: string;
};

export type ExportItem = {
  id: string;
  name: string;
  search: string;
  description: Nullable<string>;
  grouping: ExportSearchGrouping;
  status: ExportStatus;
  records: number;
  visibility?: ExportVisibility;
  files: Nullable<ExportFiles>;
  workspace: {
    id: string;
    user: {
      id: string;
      username: string;
      firstName: string;
      lastName: string;
    };
  };
  created: string;
  modified: string;
  metadata: Record<string, string>;
  integrations: {
    [key in IntegrationType]?: IntegrationSmartLeadSettingConfig;
  };
};

export type AddExportSearchResponse = ExportItem;

export type BrowseSearchExportsRequest = PaginationRequest<{
  name?: string;
  grouping?: ExportSearchGrouping;
  status?: ExportStatus;
  visibility?: ExportVisibility;
  id?: string;
}>;

export type BrowseSearchExportsResponse = PaginationResponse<
  ExportItem,
  BrowseSearchExportsMetadata
>;

export type BrowseSearchExportsMetadata = Record<
  ExportSearchGrouping | ExportVisibility,
  number
>;

export type SaveRecordKind = 'ADD' | 'REMOVE';

// todo: update email and analysis will be added as soon as the back-end provide them
export enum SaveRecordAction {
  BASIC_SAVE = 'BASIC_SAVE',
  CONTACT_REVEAL_EMAIL = 'CONTACT_REVEAL_EMAIL',
  CONTACT_ENRICH_EMAIL = 'CONTACT_ENRICH_EMAIL',
  CONTACT_REVERIFY_EMAIL = 'CONTACT_REVERIFY_EMAIL',
  CONTACT_REVEAL_ANALYSIS = 'CONTACT_REVEAL_ANALYSIS',
  CONTACT_REVEAL_PROFILE = 'CONTACT_REVEAL_PROFILE',
  CONTACT_UPDATE_PROFILE = 'CONTACT_UPDATE_PROFILE',
  CONTACT_UPDATE_JOB_CHANGE = 'CONTACT_UPDATE_JOB_CHANGE',
  // Personality analysis update. Declared, but the back-end action is not ready,
  // so no bulk action sends it — see docs/saved-records-enrich-actions.md.
  CONTACT_UPDATE_ANALYSIS = 'CONTACT_UPDATE_ANALYSIS',
  CONTACT_ENRICH_PHONE = 'CONTACT_ENRICH_PHONE',
  CONTACT_REVEAL_PHONE = 'CONTACT_REVEAL_PHONE',
  CONTACT_REVEAL_PHONE_CACHED = 'CONTACT_REVEAL_PHONE_CACHED',
  AI_POWERED_CONTACT = 'AI_POWERED_CONTACT',
  AI_POWERED_ACCOUNT = 'AI_POWERED_ACCOUNT',
}

export interface SaveRecordRequest {
  kind: SaveRecordKind;
  ids: string[];
  actions?: SaveRecordAction[];
  labels?: string[];
  overwriteOwner?: boolean;
  user?: UserRelation;
  type?: SearchMode;
}

export interface PersonEnrichableRequest {
  ids: string[];
  actions?: SaveRecordAction[];
}

// Update types the cost-estimation breakdown splits records into. The two
// profile-update reasons are mutually exclusive and both charged 0.1 once per
// updated profile version; personality analysis is independent and may overlap
// with them.
export enum PersonUpdateBreakdownKey {
  WORK_HISTORY = 'WORK_HISTORY',
  PROFILE_INSIGHTS = 'PROFILE_INSIGHTS',
  PERSONALITY_ANALYSIS = 'PERSONALITY_ANALYSIS',
}

export type PersonUpdateBreakdown = Partial<
  Record<PersonUpdateBreakdownKey, number>
>;

export interface PersonEnrichableResponse {
  total: number;
  saved: number;
  enrichable: EnrichableSavedRecord;
  unsaved: number;
  // Scoped to the requested actions. Always returned by ?action=cost-estimation,
  // which is the only endpoint this response type covers.
  breakdown: PersonUpdateBreakdown;
  skipped: number;
  totalCost: number;
}

interface EnrichableDetail {
  enrichable: number;
  unEnrichable: number;
}

export type EnrichableSavedRecord = Partial<
  Record<SaveRecordAction, EnrichableDetail>
>;

export type OnPersonChangesSocketEvent = (
  data: PersonChangesSocketMessage
) => void;

export type PersonChangesSocketMessage = {
  type: SocketMessageType.PERSON_CHANGES;
  message: Record<string, string>[];
};

export type OnReceiveSocketPersonChangesEvent = (
  data: PersonChangesSocketMessage
) => void;

export type OnCompanyChangesSocketEvent = (
  data: CompanyChangesSocketMessage
) => void;

export type CompanyChangesSocketMessage = {
  type: SocketMessageType.COMPANY_CHANGES;
  message: Record<string, string>[];
};

export type OnReceiveSocketCompanyChangesEvent = (
  data: CompanyChangesSocketMessage
) => void;

export type OnExportsChangesSocketEvent = (
  data: ExportsChangesSocketMessage
) => void;

export type ExportsChangesSocketMessage = {
  type: SocketMessageType.EXPORT_ADD | SocketMessageType.EXPORT_UPDATE;
  message: ExportItem;
};

export type CrawlRequestChangesSocketMessage = {
  type: SocketMessageType.CRAWL_UPDATE;
  message: CrawlRow;
};

export interface ApiPaymentStats {
  balance: Maybe<number>;
  usage: Maybe<{
    request: number;
    credit: number;
  }>;
}

export interface ApiLogsStats {
  summary: {
    request: number;
    credit: number;
    latency: number;
  };
  details: [
    {
      request: number;
      credit: number;
      latency: number;
      date: string;
    }
  ];
}

export enum DevelopApiKeysStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export type ApiKeysRequestParams = PaginationRequest<{
  name?: string;
}>;

export interface ApiKeys {
  id: string;
  created: string;
  modified: string; //??
  user: Workspace['user']; //??
  name: string;
  value: string;
  status: DevelopApiKeysStatus; //??
}

export interface EditApiKeysRequest {
  name: string;
}

export enum ApiGrouping {
  PROFILE_API = 'PROFILE_API',
}

export type ApiLogsRequestParams = PaginationRequest<{
  startDate?: string;
  endDate?: string;
  service?: string;
  token?: string;
  ip?: string;
  id?: string;
  status?: number;
}>;

export interface ApiLogs {
  id: string;
  created: string;
  ip: string;
  grouping: ApiGrouping;
  txId: string;
  url: string;
  status: number;
  credit: number;
  latency: number;
  request: Record<string, any>;
  service: string;
  upstream_uri: string;
  method: string;
}

export enum ApiServicePricingStrategy {
  API = 'API',
  REQUEST_SIZE = 'REQUEST_SIZE',
  RESPONSE_SIZE = 'RESPONSE_SIZE',
}

export interface DeverloperBootstrap {
  cost: {
    service: string;
    grouping: number;
    pricing: {
      strategy: ApiServicePricingStrategy;
      option: {
        value: number;
        minimum: number;
      };
    };
  }[];
}
